import {
  NOTIFICATION_BUCKET,
  NOTIFICATION_KIND,
  NOTIFICATION_TITLE_SOURCE,
  TYPE_DRIVEN_NOTIFICATION_KINDS,
} from "../constants";

const DANISH_LANGUAGE_PREFIX = "da";

const SPACE_SEPARATED_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/;

const MEDIA_KIND_MATCHERS = [
  { matches: (mime) => mime.startsWith("image/"), kind: NOTIFICATION_KIND.IMAGE },
  { matches: (mime) => mime.startsWith("video/"), kind: NOTIFICATION_KIND.VIDEO },
];

function normalizeTimestamp(value) {
  if (typeof value !== "string") return value;
  return value.replace(SPACE_SEPARATED_TIMESTAMP, "$1T$2");
}

function toTimestamp(value) {
  const normalized = normalizeTimestamp(value);
  const time = normalized ? new Date(normalized).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
}

function toAvatarLetter(name, fallback = "S") {
  const trimmed = String(name ?? "").trim();
  return trimmed ? trimmed[0].toUpperCase() : fallback;
}

function resolveIsRead(raw, bucket) {
  if (typeof raw?.is_read === "boolean") return raw.is_read;
  if (typeof raw?.data?.is_read === "boolean") return raw.data.is_read;
  return bucket === NOTIFICATION_BUCKET.READ;
}

// Legacy payloads predate has_project_access. Treating the missing field as
// "still has access" preserves the previous redirect behaviour for those rows.
function resolveProjectAccess(payload) {
  const value = payload?.has_project_access ?? payload?.hasProjectAccess;
  return typeof value === "boolean" ? value : true;
}

function toMedias(medias) {
  return (Array.isArray(medias) ? medias : []).filter(Boolean).map((media) => ({
    url: media.url ?? "",
    type: media.type ?? "",
    fileName: media.file_name ?? "",
    fileSize: Number.isFinite(media.file_size) ? media.file_size : 0,
  }));
}

export function toNotification(raw, bucket) {
  const payload = raw?.data ?? {};
  const createdAt = normalizeTimestamp(raw?.created_at ?? payload.created_at ?? null);

  return {
    id: raw?.id,
    createdAt,
    createdAtMs: toTimestamp(createdAt),
    isRead: resolveIsRead(raw, bucket),
    type: raw?.notification_type ?? payload.notification_type ?? null,
    messageType: payload.message_type ?? null,
    message: payload.message ?? "",
    medias: toMedias(payload.medias),
    localizedMessages: {
      en: payload.message_en ?? "",
      da: payload.message_da ?? "",
    },
    senderName: payload.sender_name ?? "",
    senderEmail: payload.sender_email ?? "",
    tenderId: payload.tender_id ?? "",
    tenderName: payload.tender_name ?? "",
    projectId: payload.projectId ?? payload.project_id ?? "",
    projectName: payload.project_name ?? "",
    hasProjectAccess: resolveProjectAccess(payload),
    overviewImageId: payload.overview_image_id ?? "",
    avatarLetter: toAvatarLetter(payload.sender_name),
  };
}

export function toNotificationList(rawList = [], bucket) {
  return (Array.isArray(rawList) ? rawList : [])
    .filter(Boolean)
    .map((raw) => toNotification(raw, bucket));
}

function resolveMediaKind(medias = []) {
  const mime = String(medias[0]?.type ?? "");
  const matcher = MEDIA_KIND_MATCHERS.find((candidate) => candidate.matches(mime));
  return matcher ? matcher.kind : NOTIFICATION_KIND.DOCUMENT;
}

export function resolveNotificationKind(notification) {
  if (TYPE_DRIVEN_NOTIFICATION_KINDS.includes(notification?.type)) {
    return notification.type;
  }

  const messageType = notification?.messageType;

  if (messageType && messageType !== NOTIFICATION_KIND.TEXT) return messageType;

  const hasMessage = Boolean(String(notification?.message ?? "").trim());

  if (!hasMessage && notification?.medias?.length) {
    return resolveMediaKind(notification.medias);
  }

  return messageType || NOTIFICATION_KIND.TEXT;
}

export function isUnread(notification) {
  return !notification?.isRead;
}

export function selectLocalizedMessage(notification, language = "en") {
  const isDanish = String(language).toLowerCase().startsWith(DANISH_LANGUAGE_PREFIX);
  const localized = isDanish
    ? notification?.localizedMessages?.da
    : notification?.localizedMessages?.en;

  return localized || notification?.localizedMessages?.en || notification?.message || "";
}

export function selectNotificationTitle(notification, presentation, { language = "en", t } = {}) {
  const titleSource = presentation?.titleSource;

  if (titleSource === NOTIFICATION_TITLE_SOURCE.STATIC) {
    const fallback = presentation.titleFallback ?? "";
    return typeof t === "function"
      ? t(presentation.titleKey, { defaultValue: fallback })
      : fallback;
  }

  if (titleSource === NOTIFICATION_TITLE_SOURCE.LOCALIZED_MESSAGE) {
    return selectLocalizedMessage(notification, language);
  }

  if (titleSource === NOTIFICATION_TITLE_SOURCE.SENDER) {
    return notification?.senderName ?? "";
  }

  return "";
}
